import { useMemo } from 'react';
import { useAssistantTool } from '@assistant-ui/react';
import { searchContacts } from '../../../utils/RocksetQueries/SearchQueries';
import { P } from '../../../utils/permissions';
import {
  DASHBOARD_ASSISTANT_RUNTIME_MODE,
  MAX_SEARCH_RESULTS_PER_CATEGORY,
} from '../constants';
import { stringToolParameters } from '../lib/assistantToolSchemas';
import { hasPermission, restrictedResult } from '../lib/permissions';
import { dedupeBy, getMerchantNames, limitItems } from '../lib/helpers';
import {
  createFrontendToolActionMessage,
  createFrontendToolErrorMessage,
} from '../lib/toolActionMessages';
import { buildSearchResult, createCompactSearchToolResult } from '../lib/searchResultBuilders';

export function useFindContactAction({
  activeSessionId,
  dispatchActionMessage,
  organizationName,
}) {
  const parameters = useMemo(
    () =>
      stringToolParameters({
        name: 'query',
        description: 'Contact name or email to find.',
      }),
    [],
  );

  const tool = useMemo(
    () => ({
      toolName: 'find_contact',
      type: 'frontend',
      description:
        'Deterministically search contacts in the active CharityStack organization by name or email. Use this for contact lookup questions before answering. After it returns status ok, answer from the returned contacts instead of repeating the same search.',
      parameters,
      execute: async ({ query }) => {
        const keyword = query?.trim?.();
        if (!keyword || keyword.length < 2) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'Contact search query must be at least 2 characters.',
          };
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'find_contact',
            toolResult: result,
          }));
          return result;
        }
        if (!hasPermission(P.VIEW_CONTACTS)) {
          const result = restrictedResult('contacts');
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'find_contact',
            toolResult: result,
          }));
          return result;
        }

        const merchants = await getMerchantNames(organizationName);
        if (merchants.length === 0) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'No active organization name is available for contact search.',
          };
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'find_contact',
            toolResult: result,
          }));
          return result;
        }

        const contactsRaw = await searchContacts(merchants, keyword);
        const contacts = limitItems(
          dedupeBy(contactsRaw, (contact) => contact.contactID),
          MAX_SEARCH_RESULTS_PER_CATEGORY,
        );
        const searchResult = buildSearchResult({ contacts, organizationName, query: keyword });
        const displayPayload = {
          renderer: 'dashboard_search_results',
          summary: `Searched contacts for "${keyword}"`,
          data: searchResult,
        };

        const compactResult = createCompactSearchToolResult(searchResult);
        dispatchActionMessage(createFrontendToolActionMessage({
          actionType: 'search',
          content: displayPayload.summary,
          displayPayload,
          status: compactResult.status,
          toolArgs: { query: keyword },
          toolName: 'find_contact',
          toolResult: compactResult,
        }));

        return compactResult;
      },
    }),
    [activeSessionId, dispatchActionMessage, organizationName, parameters],
  );

  useAssistantTool(tool);
}
